import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled/macro';
import {
  Autocomplete,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  AutocompleteContainer,
  SectionFlexDiv,
  SectionName,
  SectionProfile,
  SectionsPageContainer,
  SelectSectionContainer,
  SelectSectionLabel,
} from './Dashboard.Styles';

// 🆕 Estilos responsivos
const ResponsiveIframeWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* Proporção 16:9 */
  @media (max-width: 768px) {
    padding-bottom: 75%;
  }
  margin: 16px 0;
`;

const ResponsiveIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const Dashboard = () => {
  const { t } = useTranslation();
  const [sections, setSections] = useState([]);
  const [selectedArea, setSelectedArea] = useState('Geral');
  const [allDashboards, setAllDashboards] = useState([]);
  const [sectionDashboards, setSectionDashboards] = useState([]);
  const [sectionDashboardsUrl, setSectionDashboardsUrl] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const defaultDashboardInfo = {
    Geral: [
      { title: '(Geral) Colaboradores em estado crítico' },
      { title: '(Geral) Engajamento' },
      { title: '(Geral) Comparação no setor' },
      { title: '(Geral) Médias do setor' },
    ],
    'Setores ADM': [
      { title: '(Setores ADM) Colaboradores em estado crítico' },
      { title: '(Setores ADM) Comparação no setor' },
      { title: '(Setores ADM) Médias do setor' },
      { title: '(Setores ADM) Engajamento' },
    ],
    Desenvolvimento: [
      { title: '(Desenvolvimento) Colaboradores em estado crítico' },
      { title: '(Desenvolvimento) Comparação no setor' },
      { title: '(Desenvolvimento) Médias do setor' },
      { title: '(Desenvolvimento) Engajamento' },
    ],
    'Vendas e Pré Vendas': [
      { title: '(Vendas e Pré Vendas) Colaboradores em estado crítico' },
      { title: '(Vendas e Pré Vendas) Comparação no setor' },
      { title: '(Vendas e Pré Vendas) Médias do setor' },
      { title: '(Vendas e Pré Vendas) Engajamento' },
    ],
    SOS: [
      { title: '(SOS) Colaboradores em estado crítico' },
      { title: '(SOS) Comparação no setor' },
      { title: '(SOS) Médias do setor' },
      { title: '(SOS) Engajamento' },
    ],
    Projetos: [
      { title: '(Projetos) Colaboradores em estado crítico' },
      { title: '(Projetos) Comparação no setor' },
      { title: '(Projetos) Médias do setor' },
      { title: '(Projetos) Engajamento' },
    ],
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [sectionRes, dashboardRes] = await Promise.all([
          api.get('employees'),
          api.get('dashboards'),
        ]);
        setSections(sectionRes.data.users);
        setAllDashboards(dashboardRes.data.dashboards);
      } catch (error) {
        console.error('Erro ao buscar dados iniciais:', error);
        setHasError(true);
      }
    };
    fetchInitialData();
  }, []);

  const uniqueAreas = useMemo(() => {
    const areas = sections.map((s) => s.area).filter(Boolean);
    return ['Geral', ...new Set(areas)].sort((a, b) => a.localeCompare(b));
  }, [sections]);

  useEffect(() => {
    if (!selectedArea || !allDashboards.length) return;
    const filtered = allDashboards.filter((d) =>
      d?.name?.toLowerCase().includes(selectedArea.toLowerCase())
    );
    setSectionDashboards(filtered);
  }, [selectedArea, allDashboards]);

  useEffect(() => {
    const fetchEmbedUrls = async () => {
      if (!sectionDashboards.length) return;
      setIsLoading(true);
      try {
        const response = await api.post('generate-embed-dashboard-url', {
          dashboards: sectionDashboards,
        });
        const mergedData = response.data.map((resItem) => {
          const matched = sectionDashboards.find(
            (d) => d.dashboardId === resItem.dashboardId
          );
          return {
            ...resItem,
            name: matched?.name || null,
          };
        });
        setSectionDashboardsUrl(mergedData);
      } catch (error) {
        console.error('Erro ao gerar embed URLs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEmbedUrls();
  }, [sectionDashboards]);

  const handleSectionSelect = (event, newValue) => {
    setSelectedArea(newValue || 'Geral');
    setSectionDashboards([]);
    setSectionDashboardsUrl([]);
  };

  return (
    <SectionsPageContainer>
      {hasError ? (
        <Typography color="error" variant="h6" sx={{ mt: 2 }}>
          {t('sectionsPage.errorLoading') || 'Erro ao carregar as áreas'}
        </Typography>
      ) : (
        <>
          <SectionFlexDiv>
            <SelectSectionContainer>
              <SelectSectionLabel>
                {t('sectionsPage.selectSection') || 'Selecionar Área'}
              </SelectSectionLabel>
              <AutocompleteContainer>
                <Autocomplete
                  size="small"
                  value={selectedArea}
                  onChange={handleSectionSelect}
                  options={uniqueAreas}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('sectionsPage.section') || 'Área'}
                    />
                  )}
                  freeSolo
                />
              </AutocompleteContainer>
            </SelectSectionContainer>
            <Divider orientation="vertical" flexItem />
            {selectedArea && (
              <SectionProfile>
                <SectionName>{selectedArea}</SectionName>
              </SectionProfile>
            )}
          </SectionFlexDiv>

          {isLoading ? (
            <CircularProgress sx={{ mt: 4 }} />
          ) : (
            (defaultDashboardInfo[selectedArea] || []).map((info) => {
              const normalize = (str) =>
                str
                  ?.toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .replace(/\s+/g, ' ')
                  .trim();

              const matchedDashboard = sectionDashboardsUrl?.find((dash) =>
                normalize(dash.name).includes(normalize(info.title))
              );
              console.log('sectionDashboardsUrl: ', sectionDashboardsUrl);
              console.log('matchedDashboard: ', matchedDashboard);
              if (!matchedDashboard) return null;

              return (
                <ResponsiveIframeWrapper key={matchedDashboard.dashboardId}>
                  <ResponsiveIframe
                    title={info.title}
                    src={matchedDashboard.embedUrl}
                  />
                </ResponsiveIframeWrapper>
              );
            })
          )}
        </>
      )}
    </SectionsPageContainer>
  );
};

export default Dashboard;
